import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRobot,
    faSitemap,
    faProjectDiagram,
    faImage,
    faGear,
    faSpider,
    faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import ModelDropdown from './ModelDropdown';
import { Input, Toggle } from '@quyva/shared-ui';
import { ImageResolution } from '../Constants';
import { Dropdown } from '@quyva/shared-ui';
import { toSentenceCase, Features, OtherFeatures, isFeatureEnabled } from '@quyva/common';
import { useFeatureFlags } from '../context';
import { EMBEDDING_MODELS } from '../enums';
export const RepoConfig = ({
    imageResolution,
    setImageResolution,
    imageResolutionDropdown,
    modelTierRepo,
    visionModel,
    isLoadingModels,
    setModelTierRepo,
    setVisionModel,
    availableCustomModels,
    ModelTiers,
    organizationRepo,
    onChangeOrganizationRepo,
    knowledgeGraph,
    onChangeKnowledgeGraph,
    isAdmin,
    isOrganizationMember,
    isInternetRepo,
    crawlLimit,
    setCrawlLimit,
    handleCrawlLimitChange,
    embeddingModel,
    setEmbeddingModel,
    availableEmbeddingModels,
    modelsLoading,
}) => {
    const showImageResolution =
        imageResolution !== undefined &&
        setImageResolution !== undefined &&
        Array.isArray(imageResolutionDropdown);

    const showModelDropdown = true;
    const { featureFlagsData } = useFeatureFlags();

    const showOrganizationRepo =
        isOrganizationMember &&
        organizationRepo !== undefined &&
        onChangeOrganizationRepo;

    const showKnowledgeGraph =
        knowledgeGraph !== undefined && onChangeKnowledgeGraph;

    const handleSetEmbeddingModel = (val) => {
        if (typeof setEmbeddingModel === 'function') {
            setEmbeddingModel(val);
        } else {
            console.warn(
                'setEmbeddingModel not provided to RepoConfig — selection will not be stored in parent.'
            );
        }
    };

    // Build dropdown items from EMBEDDING_MODELS enum
    const embeddingValues = Object.values(EMBEDDING_MODELS ?? {});
    const embeddingDropdownItems = embeddingValues.map((val) => {
        const label = toSentenceCase(val.replace(/-/g, ' '));
        return {
            label,
            action: () => handleSetEmbeddingModel(val),
        };
    });

    const ImageDropdownItems = [
        {
            label: toSentenceCase(ImageResolution.STANDARD),
            action: () => {
                setImageResolution(ImageResolution.STANDARD);
            },
        },
        {
            label: toSentenceCase(ImageResolution.HIGH),
            action: () => {
                setImageResolution(ImageResolution.HIGH);
            },
        },
    ];

    return (
        <div className='p-4 border rounded-xl shadow-sm bg-backgrounds-white mb-6 py-2'>
            <div className='font-semibold text-texts-secondary400 pb-2 mb-3 border-b'>
                <FontAwesomeIcon
                    icon={faGear}
                    className='mr-2 text-texts-secondary300'
                />
                Configuration
            </div>

            <div className='w-full flex flex-col w-full text-texts-secondary300'>
                {showModelDropdown && 
                isFeatureEnabled(
                    [Features.OTHERS, OtherFeatures.MODEL_SELECTION],
                    featureFlagsData
                ) && (
                    <div className='w-full flex items-center mb-4'>
                        <label className='w-1/4 font-semibold text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faRobot}
                                className='mr-2 text-texts-secondary300'
                            />
                            Model
                        </label>
                        <ModelDropdown
                            modelTierRepo={modelTierRepo}
                            visionModel={visionModel}
                            isLoadingModels={isLoadingModels}
                            setModelTierRepo={setModelTierRepo}
                            setVisionModel={setVisionModel}
                            availableCustomModels={availableCustomModels}
                            ModelTiers={ModelTiers}
                            isAdmin={isAdmin}
                        />
                    </div>
                )}

                {/* Embedding Model - only visible for admins */}
                {isAdmin && (
                    <div className='w-full flex items-center mb-4'>
                        <label className='w-1/4 font-semibold text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faLayerGroup}
                                className='mr-2 text-texts-secondary300'
                            />
                            Embedding Model
                        </label>

                        {modelsLoading ? (
                            <div className='w-[250px] py-1 px-3 border rounded-md'>
                                Loading models...
                            </div>
                        ) : availableEmbeddingModels &&
                          availableEmbeddingModels.length > 0 ? (
                            <Dropdown
                                dropDownList={availableEmbeddingModels.map(
                                    (val) => ({
                                        label: toSentenceCase(
                                            val.replace(/-/g, ' ')
                                        ),
                                        action: () =>
                                            handleSetEmbeddingModel(val),
                                    })
                                )}
                                showButtonText={true}
                                buttonText={
                                    embeddingModel
                                        ? toSentenceCase(
                                              embeddingModel.replace(/-/g, ' ')
                                          )
                                        : 'Select Embedding Model'
                                }
                                buttonHoverText={'Select Embedding Model'}
                                buttonClassName={'w-[250px]'}
                                listTitleText={'Select Embedding Model'}
                                placement={'right'}
                            />
                        ) : (
                            <div className='w-[250px] py-1 px-3 border rounded-md text-texts-secondary300'>
                                No embedding models configured. Contact admin.
                            </div>
                        )}
                    </div>
                )}

                {showOrganizationRepo && (
                    <div className='w-full flex items-center mb-4'>
                        <Toggle
                            toggleText={
                                <label className='w-1/4 font-semibold text-texts-secondary300'>
                                    <FontAwesomeIcon
                                        icon={faSitemap}
                                        className='mr-2 text-texts-secondary300'
                                    />
                                    Organisation Repo
                                </label>
                            }
                            checked={organizationRepo}
                            onChange={onChangeOrganizationRepo}
                        />
                    </div>
                )}

                {showImageResolution && (
                    <div className='w-full flex items-center mb-4'>
                        <label className='w-1/4 font-semibold text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faImage}
                                className='mr-2 text-texts-secondary300'
                            />
                            Image Resolution
                        </label>
                        <Dropdown
                            dropDownList={ImageDropdownItems}
                            showButtonText={true}
                            buttonText={imageResolution}
                            buttonHoverText={'Select Image Resolution'}
                            buttonClassName={'w-[250px]'}
                            listTitleText={'Select Image Resolution'}
                            placement={'right'}
                        />
                    </div>
                )}

                {showKnowledgeGraph && (
                    <div className='w-full flex items-center mb-4'>
                        <Toggle
                            toggleText={
                                <label className='w-1/4 font-semibold text-texts-secondary300'>
                                    <FontAwesomeIcon
                                        icon={faProjectDiagram}
                                        className='mr-2 text-texts-secondary300'
                                    />
                                    Knowledge Graph
                                </label>
                            }
                            checked={knowledgeGraph}
                            onChange={onChangeKnowledgeGraph}
                        />
                    </div>
                )}
                {isAdmin && isInternetRepo && (
                    <div className='w-full flex items-center mb-4'>
                        <label className='w-1/4 font-semibold text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faSpider}
                                className='mr-2 text-texts-secondary300'
                            />
                            Set Crawl Limit
                        </label>

                        <Input
                            type='number'
                            placeholder='None'
                            value={crawlLimit ?? ''}
                            onChange={handleCrawlLimitChange}
                            className='!w-32 border border-gray-300 rounded-md px-2 py-1'
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
